import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';
import { withDemoSuffix, nonAgencyEmails, badDemoSignins, AGENCY_DOMAIN, DEMO_HINT, DEMO_SIGNIN_HINT, DEMO_SIGNIN_RULE } from '../lib/demo.js';   // Session 67: demo clients
import { ApprovalFields, APPROVAL_DEFAULTS, approvalPayload } from '../components/ApprovalBits.jsx';
import { createEntitlements, servicesPatch, ModeRadios, SEO_RECS_MODES, SEO_RECS_HINT, COURSE_MODES, COURSE_HINT, BLOG_FOCUS } from '../components/ServiceBits.jsx';   // Session 55 Part C / 57
import { CadenceTypeChoice, MonthlyFields, MONTHLY_DEFAULTS, monthlyFirstDates, minLeadDays } from '../components/CadenceBits.jsx';
import { fmtShort } from '../lib/cadence.js';
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';

// Session 5 Part B: agency-only onboarding form. Everything is validated
// again server side by portal_create_client; the checks here are just to
// save a round trip.
//
// Session 55 (Wave 16): onboarding by hand, without Stripe. A blog plan from
// the price list sets the cadence defaults and tells the client's Welcome
// screen which sections to show; "what they pay" is typed, for the clients
// overview only; "Pre-fill from their website" asks the droplet to read the
// site and fills the fields still empty; the author goes in here too. The
// create RPC is unchanged: plan, amount, author and link paths go through
// portal_hand_onboarding straight after it.

const WEEKDAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'],
  ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday'],
];
const DAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const TIMEZONES = [
  ['America/Los_Angeles', 'Pacific'], ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'], ['America/Chicago', 'Central'],
  ['America/New_York', 'Eastern'], ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'], ['Europe/London', 'London'],
];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(iso, n) {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return isoDate(d);
}

function weekdayOf(iso) {
  const idx = new Date(iso + 'T12:00:00').getDay();
  return Object.keys(DAY_INDEX).find((k) => DAY_INDEX[k] === idx);
}

function pretty(iso, time) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  const s = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return `${s}, ${time}`;
}

// Session 57 (Wave 18): the site id on Dave's platform is the website's host
// (Session 12C rule, the same strip portal_update_client does): no scheme,
// no www, no path or port. '' until the website is a full address.
function hostOf(url) {
  let h = String(url || '').trim().toLowerCase();
  if (!/^https?:\/\/[^\s/]+\.[^\s]+/.test(h)) return '';
  h = h.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^[^@/]*@/, '').replace(/[/?#].*$/, '')
    .replace(/:[0-9]*$/, '').replace(/\.$/, '').replace(/^www\./, '');
  return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/.test(h) ? h : '';
}

export function slugFromUrl(url) {
  try {
    const host = new URL(url.trim()).hostname.toLowerCase().replace(/^www\./, '');
    const parts = host.split('.');
    const base = parts.length > 1 ? parts.slice(0, -1).join('-') : parts[0];
    return base.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  } catch {
    return '';
  }
}

const splitLines = (s) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

function Rows({ rows, setRows, fields, addLabel, max }) {
  const update = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="row-list">
      {rows.map((r, i) => (
        <div className="row-item" key={i}>
          {fields.map(([k, ph, type]) => (
            <input key={k} type={type || 'text'} placeholder={ph} value={r[k] || ''}
                   onChange={(e) => update(i, k, e.target.value)} />
          ))}
          <button type="button" className="link-btn" aria-label="Remove row"
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      {(!max || rows.length < max) && (
        <button type="button" className="link-btn"
                onClick={() => setRows([...rows, Object.fromEntries(fields.map(([k]) => [k, '']))])}>
          + {addLabel}
        </button>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export default function AdminClientsNew() {
  const { isAgency, reloadClients, session } = useAuth();
  const navigate = useNavigate();

  // Next round: the hard minimum is 14 days; under 21 the form warns instead of blocking.
  // Session 15b: 14 days, or 7 for agency_only (computed below from the approval mode)
  const todayStr = useMemo(() => isoDate(new Date()), []);
  const comfortable = useMemo(() => addDays(isoDate(new Date()), 21), []);

  // Session 55: the blog plan and what they pay
  const [plans, setPlans] = useState([]);
  const [planCode, setPlanCode] = useState('');
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [payNote, setPayNote] = useState('');
  // Session 55: the website pre-fill
  const [prefill, setPrefill] = useState(null);   // null | {state:'running'|'done'|'failed', filled?:[], error?}
  const prefillTimer = useRef(null);
  const prefillGone = useRef(false);               // set on unmount: a late poll result is dropped
  // review 2: the poll fires minutes after the click, so the result is
  // applied against the values at THAT moment (this ref), not the ones
  // captured when the button was clicked.
  const formRef = useRef({});
  const [allowlist, setAllowlist] = useState([]);   // suggested link paths, from the pre-fill
  // Session 55: the author (people.byline, the shape Edit client uses)
  const [byName, setByName] = useState('');
  const [byTitle, setByTitle] = useState('');
  const [byBio, setByBio] = useState('');
  const [byUrl, setByUrl] = useState('');

  // Business
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [services, setServices] = useState([{ url: '', description: '' }]);
  const [serviceArea, setServiceArea] = useState('');
  const [industry, setIndustry] = useState('');
  // Market
  const [competitors, setCompetitors] = useState([]);
  const [avoidTopics, setAvoidTopics] = useState('');
  const [proof, setProof] = useState([]);
  // Voice
  const [voiceNotes, setVoiceNotes] = useState('');
  const [usePhrases, setUsePhrases] = useState('');
  const [avoidPhrases, setAvoidPhrases] = useState('');
  // People
  const [noticeEmails, setNoticeEmails] = useState('');
  const [signinEmails, setSigninEmails] = useState('');
  const [approver, setApprover] = useState('');
  // Setup
  // Session 12 Part A: optional analytics; saving queues the droplet check
  const [ga4Property, setGa4Property] = useState('');
  const [gscProperty, setGscProperty] = useState('');
  const [gmbAudit, setGmbAudit] = useState(false);
  // Session 55 Part C: the other services, so one screen sets up everything
  // Leena is switching on and in which mode (Edit client had these; Add client
  // only had GMB Audit). Stored as entitlement codes, see ServiceBits.jsx.
  const [newsletterOn, setNewsletterOn] = useState(false);
  const [seoRecs, setSeoRecs] = useState('off');
  const [courses, setCourses] = useState('off');
  const [adapter, setAdapter] = useState('handoff');
  const [blogFocus, setBlogFocus] = useState('seo');   // Session 57 (Wave 18): pack.blog.focus
  const [platform, setPlatform] = useState('other');   // website platform (pack.platform)
  const [fixesBy, setFixesBy] = useState('client');    // Session 23: pack.seo_fixes_by
  const [maxInBody, setMaxInBody] = useState(2);   // Session 6 Part E: images per post
  const [approval, setApproval] = useState(APPROVAL_DEFAULTS);   // Session 13
  // Cadence
  const [firstPost, setFirstPost] = useState('');
  const [ppw, setPpw] = useState(1);
  const [days, setDays] = useState(['tue', 'thu']);
  const [qLead, setQLead] = useState(7);
  const [aLead, setALead] = useState(2);
  const [tz, setTz] = useState('America/Los_Angeles');
  // Session 55 Part B: fast start, on by default for a client created here
  // (existing clients onboarded by hand). Off = the ordinary 14-day lead.
  const [fastStart, setFastStart] = useState(true);
  const [qs] = useSearchParams();
  const [demo, setDemo] = useState(qs.get('demo') === '1');   // Session 67: a demo client (see lib/demo.js); Session 68: ?demo=1 ticks it
  // Session 15: monthly cadence ("Twice a month (or more) on set dates")
  const [cadType, setCadType] = useState('weekly');
  const [monthDays, setMonthDays] = useState(MONTHLY_DEFAULTS.month_days);
  const [monthWd, setMonthWd] = useState(MONTHLY_DEFAULTS.publish_weekday);   // '' = exact date (15b)
  const [lastWd, setLastWd] = useState(MONTHLY_DEFAULTS.publish_weekday);
  const [monthPpd, setMonthPpd] = useState(1);                                  // posts on each date (15b)
  const [kickoff, setKickoff] = useState('');                                   // kick-off batch date (15b)

  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [createdId, setCreatedId] = useState(null);   // review 11: created, but the follow-up save failed
  const saved = useSaveStatus();   // Session 24: the shared Saved / Couldn't save line

  // Session 55: the blog plans, from the same list the sign-up page reads.
  useEffect(() => {
    supabase.from('v_portal_plans').select('plan_code, name, blurb, cadence_hint, price_monthly_cents, sellable, sort')
      .eq('group_code', 'blogs').order('sort')
      .then(({ data }) => setPlans(data || []));
    return () => { prefillGone.current = true; if (prefillTimer.current) clearTimeout(prefillTimer.current); };
  }, []);

  // Picking a plan sets the cadence it implies; everything stays editable.
  function pickPlan(code) {
    setPlanCode(code);
    const p = plans.find((x) => x.plan_code === code);
    const hint = p?.cadence_hint || null;
    if (!hint) return;
    setFirstPost('');
    if (hint.type === 'monthly') {
      setCadType('monthly');
      if (Array.isArray(hint.month_days) && hint.month_days.length) setMonthDays(hint.month_days);
      if (hint.posts_per_date) setMonthPpd(Number(hint.posts_per_date));
    } else {
      setCadType('weekly');
      if (hint.posts_per_week) setPpw(Number(hint.posts_per_week));
    }
    // what they pay is typed, never assumed from the list price (review 18)
  }

  // Session 55: ask the droplet to read the site, then fill what is still empty.
  async function runPrefill() {
    setBanner(null);
    if (!/^https?:\/\/[^\s/]+\.[^\s]+/i.test(website.trim())) {
      return setBanner({ tone: 'err', text: 'Add the website first, as a full address like https://example.com' });
    }
    const { data: jobId, error } = await supabase.rpc('portal_request_prefill', { p_website: website.trim() });
    if (error) return setBanner({ tone: 'err', text: error.message });
    setPrefill({ state: 'running', jobId, since: Date.now() });
    const poll = async () => {
      if (prefillGone.current) return;
      const { data, error: e } = await supabase.rpc('portal_prefill_result', { p_job_id: jobId });
      if (prefillGone.current) return;
      if (e) { setPrefill({ state: 'failed', error: e.message }); return; }
      if (data?.status === 'done' && data.result) { applyPrefill(data.result); return; }
      if (data?.status === 'failed') { setPrefill({ state: 'failed', error: data.error || 'The site could not be read.' }); return; }
      if (Date.now() - (prefillRef.current?.since || Date.now()) > 8 * 60 * 1000) {
        setPrefill({ state: 'failed', error: 'It took too long. Fill the form in by hand, or try again.' });
        return;
      }
      prefillTimer.current = setTimeout(poll, 5000);
    };
    prefillTimer.current = setTimeout(poll, 5000);
  }
  const prefillRef = useRef(null);
  prefillRef.current = prefill;

  function applyPrefill(r) {
    const cur = formRef.current;   // what is on the form right now
    const filled = [];
    const put = (label, have, val, set) => { if (!String(have || '').trim() && String(val || '').trim()) { set(val); filled.push(label); } };
    put('what they do', cur.description, r.description, setDescription);
    put('service area', cur.serviceArea, r.service_area, setServiceArea);
    put('industry', cur.industry, r.industry, setIndustry);
    put('voice notes', cur.voiceNotes, r.voice_notes, setVoiceNotes);
    if (!(cur.services || []).some((x) => x.url.trim()) && (r.services || []).length) {
      setServices(r.services.map((x) => ({ url: x.url || '', description: x.description || '' }))); filled.push('services');
    }
    if (!(cur.competitors || []).length && (r.competitors || []).length) { setCompetitors(r.competitors); filled.push('competitors'); }
    if (!(cur.proof || []).length && (r.proof || []).length) { setProof(r.proof.map((x) => ({ text: x.text || '', url: x.url || '' }))); filled.push('proof'); }
    const by = r.byline || {};
    put('author name', cur.byName, by.name, setByName);
    put('author title', cur.byTitle, by.title, setByTitle);
    put('author bio', cur.byBio, by.bio, setByBio);
    put('About page', cur.byUrl, by.url, setByUrl);
    if (!(cur.allowlist || []).length && (r.allowlist || []).length) { setAllowlist(r.allowlist); filled.push('pages we may link to'); }
    setPrefill({ state: 'done', filled, pages: r.pages_read });
  }
  formRef.current = { description, serviceArea, industry, voiceNotes, services, competitors, proof, byName, byTitle, byBio, byUrl, allowlist };

  if (!isAgency) return <Navigate to="/" replace />;

  const effectiveSlug = slugTouched ? slug : slugFromUrl(website);
  const siteId = hostOf(website);   // Session 57: the site id on Dave's platform
  const pickedDays = days.slice(0, ppw);
  const firstDay = firstPost ? weekdayOf(firstPost) : null;
  const minLead = minLeadDays(approval.mode, fastStart);
  const minFirst = addDays(todayStr, minLead);
  const agencyOnly = approval.mode === 'agency_only';
  const monthly = cadType === 'monthly';
  // Session 15b: with a kick-off batch the first post is the kick-off.
  const firstDate = monthly && kickoff ? kickoff : firstPost;
  const dayMismatch = !monthly && firstPost && !pickedDays.includes(firstDay);
  // Session 15: a monthly first post date is picked from the generated dates.
  const monthlyCad = { month_days: monthDays, publish_weekday: monthWd, posts_per_date: monthPpd, kickoff_date: kickoff || null };
  const monthlyFirst = monthly && monthDays.length ? monthlyFirstDates(monthlyCad, minFirst, addDays(minFirst, 120)) : [];
  const monthlyMismatch = monthly && !kickoff && firstPost && !monthlyFirst.includes(firstPost);

  function onFirstPost(v) {
    setFirstPost(v);
    // Keep the first publish day in step with the chosen date.
    if (v) {
      const wd = weekdayOf(v);
      if (!days.slice(0, ppw).includes(wd)) setDays([wd, days[1] === wd ? days[0] : days[1]]);
    }
  }

  const questionsDate = firstDate ? addDays(firstDate, -Number(qLead || 0)) : '';

  async function onSubmit(e) {
    e.preventDefault();
    setBanner(null);
    const notice = splitLines(noticeEmails);
    if (!name.trim() || !website.trim()) return setBanner({ tone: 'err', text: 'Please add the business name and website.' });
    if (!notice.length) return setBanner({ tone: 'err', text: 'Please add at least one notice email.' });
    if (demo) {
      // Session 67: a demo never reaches a real inbox.
      const outside = nonAgencyEmails([...notice, ...splitLines(signinEmails), approver.trim()]);
      if (outside.length) return setBanner({ tone: 'err', text: `A demo client can only have Digital Lotus contacts (@${AGENCY_DOMAIN}), so nothing reaches a real inbox. Not allowed: ${outside.join(', ')}.` });
      // Session 68b: the sign-in is the login used on the call; it must be its own (plus) address.
      const signins = splitLines(signinEmails);
      if (!signins.length) return setBanner({ tone: 'err', text: `A demo needs a portal sign-in: the login you use on the call. ${DEMO_SIGNIN_RULE}` });
      const badSignin = badDemoSignins(signins, session?.user?.email);
      if (badSignin.length) return setBanner({ tone: 'err', text: `${DEMO_SIGNIN_RULE} Not allowed: ${badSignin.join(', ')}.` });
    }
    // Session 68: on the call the client login approves the plan and the draft on screen.
    if (demo && approval.mode === 'agency_only') return setBanner({ tone: 'err', text: 'A demo cannot be agency-only approval: on the call the client login approves the plan and the draft on screen. Pick "The client approves" or the deadline mode.' });
    if (!demo && adapter === 'partner_api' && !siteId) return setBanner({ tone: 'err', text: 'Astro / Dave needs the website as a full address, like https://example.com: its host is the site id on Dave\'s platform.' });
    if (amount !== '' && !(Number(amount) >= 0)) return setBanner({ tone: 'err', text: 'What they pay should be a number, like 100 or 35.50.' });
    if (!firstDate) return setBanner({ tone: 'err', text: 'Please pick a first post date.' });
    if (firstDate < minFirst) return setBanner({ tone: 'err', text: `The ${monthly && kickoff ? 'kick-off' : 'first post'} date needs to be at least ${minLead} days out.` });
    if (agencyOnly && questionsDate < todayStr) return setBanner({ tone: 'err', text: `The questions go out ${qLead} days before each post, so the first post needs to be at least ${qLead} days out. Pick a later date or shorten the questions lead time.` });
    if (dayMismatch) return setBanner({ tone: 'err', text: 'The first post date should fall on one of the publish days.' });
    if (monthly && !monthDays.length) return setBanner({ tone: 'err', text: 'Pick between 1 and 4 days of the month.' });
    if (monthlyMismatch) return setBanner({ tone: 'err', text: 'The first post date should be one of the publish dates.' });

    const payload = {
      name: demo ? withDemoSuffix(name) : name.trim(),   // Session 67: the "(Demo)" suffix is the marker
      website: website.trim(),
      client_id: effectiveSlug,
      description: description.trim(),
      services: services.filter((s) => s.url.trim()),
      service_area: serviceArea.trim(),
      industry: industry.trim(),
      competitors: competitors.filter((c) => c.name.trim() || c.url.trim()),
      avoid_topics: avoidTopics.trim(),
      proof: proof.filter((p) => p.text.trim()),
      voice_notes: voiceNotes.trim(),
      use_phrases: splitLines(usePhrases),
      avoid_phrases: splitLines(avoidPhrases),
      notice_emails: notice,
      signin_emails: splitLines(signinEmails),
      approver_email: approver.trim(),
      ...((ga4Property.trim() || gscProperty.trim())
        ? { analytics: { ga4_property_id: ga4Property.trim(), gsc_site_url: gscProperty.trim() } } : {}),
      publish_adapter: demo ? 'handoff' : adapter,   // Session 67: a demo never publishes
      // Session 57 (Wave 18): Astro / Dave. The RPC stores the row as handoff
      // with the partner config armed and queues the connection check; the
      // droplet chains check -> sandbox test -> on and emails Leena once.
      ...(!demo && adapter === 'partner_api' ? { publish: { partner: { provider: 'bbw', site_id: siteId } } } : {}),
      blog: { focus: blogFocus },   // Session 57 (Wave 18)
      platform,
      seo_fixes_by: fixesBy,
      images: { max_in_body: Number(maxInBody) },
      approval: approvalPayload(approval),
      entitlements: createEntitlements({ gmbAudit, seoRecs }),   // Session 55 Part C; newsletter + courses follow below
      cadence: {
        first_post_date: firstDate,
        ...(monthly
          ? { type: 'monthly', month_days: monthDays, posts_per_date: monthPpd,
              ...(monthWd ? { publish_weekday: monthWd } : {}), ...(kickoff ? { kickoff_date: kickoff } : {}) }
          : { posts_per_week: ppw, publish_weekdays: pickedDays }),
        questions_lead_days: Number(qLead),
        approve_by_lead_days: Number(aLead),
        timezone: tz,
        fast_start: demo ? true : fastStart,   // Session 55 Part B; Session 68: a demo is always fast start
      },
    };

    setBusy(true); saved.start();
    try {
      const { data, error } = await supabase.rpc('portal_create_client', { p: payload });
      if (error) throw error;
      setCreatedId(data);   // review pass 2: the client exists from here on, whatever happens below
      // Session 55: the follow-up saves the create RPC does not take. Both run
      // even if one fails (review C1: the hand save failing used to skip the
      // services, and nobody was told); the client exists, so failures are
      // reported in one banner and Edit client can finish.
      //   1. services (Part C): the newsletter module and the courses mode,
      //      which portal_create_client refuses (its allowed set stops at SEO
      //      recommendations). First, so the entitlements list it sends is
      //      never behind an analytics job that the droplet finished meanwhile
      //      (review C2; the job needs a reactor tick, this call needs seconds).
      //   2. plan, amount, author and link paths (Part A).
      const problems = [];
      const svc = servicesPatch({ gmbAudit, newsletter: newsletterOn, courses });
      if (svc) {
        const { error: e3 } = await supabase.rpc('portal_update_client', { p_client_id: data, p_patch: svc });
        if (e3) problems.push(`the newsletter or AI courses did not switch on (${e3.message})`);
      }
      const hand = {
        ...(planCode ? { plan_code: planCode } : {}),
        ...(amount !== '' ? { amount_cents: String(Math.round(Number(amount) * 100)), period, note: payNote.trim() } : {}),
        ...((byName || byTitle || byBio || byUrl) ? { byline: { name: byName.trim(), title: byTitle.trim(), bio: byBio.trim(), url: byUrl.trim() } } : {}),
        ...(allowlist.length ? { allowlist } : {}),
        ...(prefill?.state === 'done' ? { prefilled_from_website: true } : {}),
      };
      if (Object.keys(hand).length) {
        const { error: e2 } = await supabase.rpc('portal_hand_onboarding', { p_client_id: data, p: hand });
        if (e2) problems.push(`the plan, amount, author or link paths did not save (${e2.message})`);
      }
      if (problems.length) {
        // review 11: stay here so the message is read; Create stays disabled
        // (createdId) so a second click cannot hit "client ID already taken".
        saved.fail(problems.map((t) => t.replace(/ \(.*\)$/, '')).join('; '));
        await reloadClients(data);
        setBanner({ tone: 'err', text: `${name.trim()} was created${agencyOnly ? '' : ' and invited'}, but ${problems.join(', and ')}. Finish under Settings → Edit client.` });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCreatedId(data);
        return;
      }
      saved.ok();
      await reloadClients(data);
      navigate(clientPath(`/admin/clients/${data}/onboarding`, data));
    } catch (err) {
      saved.fail(err.message || String(err));
      setBanner({ tone: 'err', text: err.message || String(err) });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="client-form" onSubmit={onSubmit}>
      <h1>Add new client</h1>
      <p className="lede">
        Fill in what you know, or add the website and let us read it. The client gets an invite and a
        Welcome screen showing everything here, to confirm or correct. Anything left blank can be added later.
      </p>

      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      {createdId && (
        <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }}>
          <Link className="btn btn-primary" to={clientPath(`/admin/clients/${createdId}/onboarding`, createdId)}>Open their steps</Link>
          <Link className="btn" to={clientPath(`/admin/clients/${createdId}/settings?tab=edit`, createdId)}>Edit client</Link>
        </div>
      )}

      <section className="form-section">
        <h2>Business</h2>
        <Field label="Business name">
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        {/* Session 67: a client for recordings and sales calls (lib/demo.js) */}
        <div className="field">
          <label className="check">
            <input type="checkbox" checked={demo} disabled={busy}
                   onChange={(e) => { setDemo(e.target.checked); if (e.target.checked) { setAdapter('handoff'); setFastStart(true); } }} />
            {' '}<span>Demo client (for recordings and sales calls)</span>
          </label>
          <span className="field-hint">{DEMO_HINT}</span>
          {demo && <span className="field-hint">{DEMO_SIGNIN_HINT}</span>}
        </div>
        <Field label="Website">
          <input type="url" placeholder="https://example.com" value={website}
                 onChange={(e) => setWebsite(e.target.value)} required />
        </Field>
        {/* Session 55: the droplet reads the site and fills what is still empty */}
        <div className="field">
          <div className="inline-fields" style={{ alignItems: 'center' }}>
            <button type="button" className="btn" disabled={busy || prefill?.state === 'running'} onClick={runPrefill}>
              {prefill?.state === 'running' ? 'Reading their website…' : prefill ? 'Read their website again' : 'Pre-fill from their website'}
            </button>
            {prefill?.state === 'running' && <span className="field-hint">Usually one to three minutes. You can keep filling in the rest; only empty fields get filled.</span>}
            {prefill?.state === 'done' && (
              <span className="field-hint">
                {prefill.filled.length ? `Filled in: ${prefill.filled.join(', ')}` : 'Nothing new to fill in'}
                {prefill.pages ? ` (${prefill.pages} pages read).` : '.'} Check everything before you save.
              </span>
            )}
            {prefill?.state === 'failed' && <span className="field-hint err">{prefill.error}</span>}
          </div>
        </div>
        <Field label="Client ID" hint="Lowercase letters, numbers and hyphens. Used in links and file paths, so it can't change later.">
          <input value={effectiveSlug} pattern="[a-z0-9]+(-[a-z0-9]+)*"
                 onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} required />
        </Field>
        <Field label="What they do" hint="Two or three sentences.">
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="field">
          <span className="field-label">Services and money pages</span>
          <Rows rows={services} setRows={setServices} addLabel="Add a page"
                fields={[['url', 'https://example.com/service', 'url'], ['description', 'One line about this service']]} />
        </div>
        <Field label="Service area" hint={'Cities or regions, separated by commas. Or "national".'}>
          <input value={serviceArea} onChange={(e) => setServiceArea(e.target.value)} />
        </Field>
        <Field label="Industry">
          <input value={industry} onChange={(e) => setIndustry(e.target.value)} />
        </Field>
      </section>

      <section className="form-section">
        <h2>Market</h2>
        <div className="field">
          <span className="field-label">Competitors (up to 5)</span>
          <Rows rows={competitors} setRows={setCompetitors} addLabel="Add a competitor" max={5}
                fields={[['name', 'Name'], ['url', 'https://competitor.com', 'url']]} />
        </div>
        <Field label="Topics to avoid">
          <textarea rows={2} value={avoidTopics} onChange={(e) => setAvoidTopics(e.target.value)} />
        </Field>
        <div className="field">
          <span className="field-label">Proof we can use</span>
          <span className="field-hint">Case studies, stats, testimonials, awards. Add a link where there is one.</span>
          <Rows rows={proof} setRows={setProof} addLabel="Add proof"
                fields={[['text', 'What it is'], ['url', 'https://… (optional)', 'url']]} />
        </div>
      </section>

      <section className="form-section">
        <h2>Voice</h2>
        <Field label="Brand voice notes">
          <textarea rows={3} value={voiceNotes} onChange={(e) => setVoiceNotes(e.target.value)} />
        </Field>
        <Field label="Words and phrases to use" hint="One per line or comma separated.">
          <textarea rows={2} value={usePhrases} onChange={(e) => setUsePhrases(e.target.value)} />
        </Field>
        <Field label="Words and phrases to avoid" hint="One per line or comma separated.">
          <textarea rows={2} value={avoidPhrases} onChange={(e) => setAvoidPhrases(e.target.value)} />
        </Field>
      </section>

      <section className="form-section">
        <h2>The author</h2>
        <p className="field-hint">Who the posts are by. The byline and the author schema use this; the About page is where the byline links. The client can correct it on their Welcome screen.</p>
        <div className="inline-fields">
          <Field label="Name"><input value={byName} onChange={(e) => setByName(e.target.value)} placeholder="Dr. Jane Doe, DPT" /></Field>
          <Field label="Title"><input value={byTitle} onChange={(e) => setByTitle(e.target.value)} placeholder="Founder of Example Co" /></Field>
        </div>
        <Field label="One-line bio"><textarea rows={2} value={byBio} onChange={(e) => setByBio(e.target.value)} /></Field>
        <Field label="About page URL"><input type="url" value={byUrl} onChange={(e) => setByUrl(e.target.value)} placeholder="https://example.com/about" /></Field>
        {allowlist.length > 0 && (
          <div className="field">
            <span className="field-label">Pages we may link to</span>
            <span className="field-hint">From their site. The service pages above are always allowed; untick anything a post should not link to.</span>
            <div className="onb-words">
              {allowlist.map((p) => (
                <button key={p} type="button" className="onb-word onb-word-on" aria-pressed="true" title="Remove"
                        onClick={() => setAllowlist(allowlist.filter((x) => x !== p))}>{p} ×</button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="form-section">
        <h2>People</h2>
        <Field label="Notice emails" hint="Who hears about plans, questions and drafts. One per line or comma separated.">
          <textarea rows={2} value={noticeEmails} onChange={(e) => setNoticeEmails(e.target.value)} required />
        </Field>
        <Field label="Portal sign-in emails" hint="Leave blank to use the notice emails. Each one gets a password setup email.">
          <textarea rows={2} value={signinEmails} onChange={(e) => setSigninEmails(e.target.value)} />
        </Field>
        <Field label="Approver email" hint="Leave blank to use the first notice email.">
          <input type="email" value={approver} onChange={(e) => setApprover(e.target.value)} />
        </Field>
      </section>

      <section className="form-section">
        <h2>Setup</h2>
        <div className="field">
          <span className="field-label">Analytics (optional)</span>
          <span className="field-hint">Add ops@digitallotusmarketing.com as a Viewer in GA4 and a user in Search Console first. Saving runs a connection check; when it passes the history loads and the Results tab switches on. You can add these later in Settings.</span>
        </div>
        <Field label="GA4 property ID" hint="The number in GA4 Admin → Property details (not the account ID).">
          <input inputMode="numeric" placeholder="123456789" value={ga4Property} onChange={(e) => setGa4Property(e.target.value)} />
        </Field>
        <Field label="Search Console property" hint="Exactly as Search Console shows it.">
          <input placeholder="sc-domain:example.com" value={gscProperty} onChange={(e) => setGscProperty(e.target.value)} />
        </Field>
        <Field label="Website platform" hint="What their site runs on. An Astro site on Dave's platform publishes automatically when “How posts go live” below says so; every other non-repo client is delivered as a handoff package (Squarespace gets a paste-ready pack).">
          <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="squarespace">Squarespace</option>
            <option value="wordpress">WordPress</option>
            <option value="astro">Astro</option>
            <option value="react">React / custom</option>
            <option value="lovable">Lovable (synced with GitHub)</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Who makes the SEO fixes" hint="The owner of every item on the SEO checklist. You can change single items later.">
          <select value={fixesBy} onChange={(e) => setFixesBy(e.target.value)}>
            <option value="client">Client (self-serve)</option>
            <option value="agency">Digital Lotus</option>
          </select>
        </Field>
        <Field label="How posts go live"
               hint={adapter === 'partner_api'
                 ? (siteId
                   ? `Site id on Dave's platform: ${siteId} (the website's host; Dave must have it and sandbox.${siteId} on our key). As soon as you save, we check the connection and run a test post on his sandbox. Automatic publishing switches on by itself when both pass, and you get one email either way. Until then posts are delivered as handoff packages. You can watch or stop it under Settings → Publishing.`
                   : 'Add the website above as a full address first: its host is the site id on Dave\'s platform.')
                 : undefined}>
          {/* Session 12C: Handoff or WordPress (no Repo). Session 57 (Wave 18):
              Astro / Dave arms the check -> test -> on chain at creation;
              Settings → Publishing still shows and can stop it. */}
          {demo ? (
            <p className="hint" style={{ margin: 0 }}>Handoff. A demo client never publishes anywhere.</p>
          ) : (
          <select value={adapter} onChange={(e) => { setAdapter(e.target.value); if (e.target.value === 'partner_api' && platform === 'other') setPlatform('astro'); }}>
            <option value="handoff">Handoff: we deliver the ready-to-post package</option>
            <option value="partner_api">Astro / Dave (Build A Better Website): we publish automatically</option>
            <option value="wordpress">WordPress: we publish straight to the site through its own login (handoff until that login is saved on the droplet)</option>
          </select>
          )}
        </Field>
        <Field label="Images per post" hint="Hero + N in-body. In-body slots can always be skipped; the hero is required. A photography client will want more.">
          <div className="inline-fields" style={{ alignItems: 'center' }}>
            <span>Hero +</span>
            <input type="number" min={0} max={8} style={{ maxWidth: 90 }} value={maxInBody} onChange={(e) => setMaxInBody(e.target.value)} />
            <span>in-body</span>
          </div>
        </Field>
        <ApprovalFields value={approval} onChange={setApproval} disabled={busy} />
        {approval.mode === 'agency_only' && (
          <p className="field-hint" style={{ marginTop: -6 }}>No portal invite or welcome email will be sent. You can still keep their emails here for later.</p>
        )}
        {/* Session 55 Part C: every service and its mode, the same switches as
            Edit client. Analytics stays automatic. */}
        <div className="field">
          <span className="field-label">Services</span>
          <span className="field-hint">What this client is signed up for. Everything here can be changed later under Settings → Edit client.</span>
          <label className="check"><input type="checkbox" checked readOnly /> <span>SEO Blogs (always on; the plan and cadence are below)</span></label>
          <label className="check"><input type="checkbox" checked={gmbAudit} disabled={busy} onChange={(e) => setGmbAudit(e.target.checked)} /> <span>GMB Audit</span></label>
          <label className="check"><input type="checkbox" checked={newsletterOn} disabled={busy} onChange={(e) => setNewsletterOn(e.target.checked)} /> <span>Newsletter</span></label>
          {newsletterOn && <span className="field-hint">A newsletter is made from every post that goes out. {agencyOnly ? 'Nobody signs in for an agency-only client, so you set the template, logo and colours under Settings → Edit client before the first post.' : 'The client picks the template, logo and colours on their Welcome screen; you can set or change them under Edit client.'}</span>}
          <label className="check disabled"><input type="checkbox" disabled /> <span>Analytics (switches on by itself once the connection check passes)</span></label>
        </div>
        <div className="field">
          <span className="field-label">SEO recommendations</span>
          <ModeRadios name="new-seo-recs-mode" modes={SEO_RECS_MODES} hints={SEO_RECS_HINT} value={seoRecs} onChange={setSeoRecs} disabled={busy} hintClass="field-hint"
                      after="The client sees a list only after you release it (SEO checklist tab)." />
        </div>
        <div className="field">
          <span className="field-label">AI courses</span>
          <ModeRadios name="new-courses-mode" modes={COURSE_MODES} hints={COURSE_HINT} value={courses} onChange={setCourses} disabled={busy} hintClass="field-hint"
                      after="Everyone signed in for this client gets it; progress is per person." />
        </div>
      </section>

      <section className="form-section">
        <h2>Plan and payment</h2>
        <Field label="Blog plan" hint="From the price list. Sets the cadence below (you can still change it) and tells their Welcome screen which questions to ask. Stripe is not involved.">
          <select value={planCode} onChange={(e) => pickPlan(e.target.value)}>
            <option value="">Choose a plan…</option>
            {plans.map((p) => (
              <option key={p.plan_code} value={p.plan_code}>{p.name} (${(p.price_monthly_cents / 100).toFixed(0)} a month list price)</option>
            ))}
          </select>
        </Field>
        {/* Session 57 (Wave 18): blog focus, the same radios as Edit client */}
        <div className="field approval-fields" role="radiogroup" aria-label="Blog focus">
          <span className="field-label">Blog focus</span>
          <span className="field-hint">What the planner builds this client's posts around. Changeable later under Edit client, where the SEO keywords to avoid live too.</span>
          {BLOG_FOCUS.map(([k, label, hint]) => (
            <label key={k} className="check approval-option">
              <input type="radio" name="new-blog-focus" value={k} checked={blogFocus === k} disabled={busy}
                     onChange={() => setBlogFocus(k)} />
              <span>
                <span className="approval-option-label">{label}</span>
                <span className="field-hint">{hint}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="inline-fields">
          <Field label="What they pay" hint="Dollars, the total for everything switched on above. For your own records: shown on the Clients page only, never to the client.">
            <input inputMode="decimal" placeholder="100" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ maxWidth: 140 }} />
          </Field>
          <Field label="Per">
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="monthly">month</option>
              <option value="annual">year</option>
              <option value="one_off">one-off</option>
            </select>
          </Field>
          <Field label="Note" hint="Optional: how they are billed, discounts, anything you want to remember.">
            <input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Invoiced by hand, $100/mo agreed 2026-09-30" />
          </Field>
        </div>
      </section>

      <section className="form-section">
        <h2>Cadence</h2>
        {/* Session 55 Part B: the fast-start switch. What it changes is spelled
            out here because the 2-day clocks only exist in the "approve or we go
            ahead" mode; in the other modes it is the shorter lead and the early
            questions only. */}
        <div className="field">
          <label className="check"><input type="checkbox" checked={demo || fastStart} disabled={busy || demo} onChange={(e) => setFastStart(e.target.checked)} /> <span>Fast start</span></label>
          <span className="field-hint">
            {fastStart
              ? `First post as soon as 7 days out. The first post's questions go out the moment the plan is approved and its draft is written as soon as the answers land. ${approval.mode === 'client_deadline' ? 'The first plan and those first questions approve themselves after 2 days of silence, with a reminder the day before (none when they go out late in the evening).' : approval.mode === 'agency_only' ? 'You answer the first questions yourself, straight away.' : 'With this approval mode nothing approves itself, so the pace depends on the client answering. If they pick "Keep things moving" on their Welcome screen, the first plan and first questions get the 2-day clock.'} Everything after the first post runs on the normal cadence. You can switch it off later under Edit client.`
              : 'Off: the first post is at least 14 days out and the first questions go out on the normal lead time.'}
          </span>
        </div>
        <CadenceTypeChoice value={cadType} onChange={(t) => { setCadType(t); setFirstPost(''); }} />
        {monthly && (
          <MonthlyFields monthDays={monthDays} weekday={monthWd} postsPerDate={monthPpd} kickoff={kickoff}
                         kickoffMin={minFirst} weekdays={WEEKDAYS} previewFrom={minFirst} lastWeekday={lastWd}
                         onDays={(d) => { setMonthDays(d); setFirstPost(''); }}
                         onWeekday={(w) => { setMonthWd(w); if (w) setLastWd(w); setFirstPost(''); }}
                         onPostsPerDate={setMonthPpd}
                         onKickoff={(d) => { setKickoff(d); setFirstPost(''); }} />
        )}
        <Field label="First post date"
               hint={agencyOnly
                 ? `At least ${minLead} days out (agency approval only: you answer the questions${fastStart ? ' for the first post straight away, then' : ','} ${qLead} days before each post).`
                 : fastStart
                   ? `At least ${minLead} days out (fast start). The site analysis, your approval of the strategy and the client's approval of the first plan still come first: 7 to 10 days is the target when everyone answers quickly.`
                   : `At least ${minLead} days out. Three weeks is comfortable: the site analysis, your approval of the strategy, and the client's approval of the first plan all come first.`}>
          {monthly && kickoff ? (
            <span className="field-hint">The kick-off batch: {fmtShort(kickoff)}</span>
          ) : monthly ? (
            <select value={firstPost} onChange={(e) => setFirstPost(e.target.value)} required>
              <option value="">{monthDays.length ? 'Pick a publish date' : 'Pick the days of the month first'}</option>
              {monthlyFirst.map((d) => <option key={d} value={d}>{fmtShort(d)}</option>)}
            </select>
          ) : (
            <input type="date" min={minFirst} value={firstPost} onChange={(e) => onFirstPost(e.target.value)} required />
          )}
        </Field>
        {!agencyOnly && !fastStart && firstDate && firstDate >= minFirst && firstDate < comfortable && (
          <div className="banner banner-gate">
            Tight timeline: analysis, plan approvals and first questions all need to happen in the next two weeks.
          </div>
        )}
        {!monthly && <>
        <Field label="Posts per week">
          <select value={ppw} onChange={(e) => setPpw(Number(e.target.value))}>
            <option value={1}>1</option>
            <option value={2}>2</option>
          </select>
        </Field>
        <div className="field">
          <span className="field-label">Publish {ppw === 1 ? 'day' : 'days'}</span>
          <div className="inline-fields">
            {Array.from({ length: ppw }).map((_, i) => (
              <select key={i} value={days[i]} onChange={(e) => setDays(days.map((d, j) => (j === i ? e.target.value : d)))}>
                {WEEKDAYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            ))}
          </div>
          {dayMismatch && <span className="field-hint err">The first post date is a {WEEKDAYS.find(([k]) => k === firstDay)?.[1]}. Pick that as a publish day or change the date.</span>}
        </div>
        </>}
        <div className="inline-fields">
          <Field label="Questions go out (days before publish)">
            <input type="number" min={3} max={28} value={qLead} onChange={(e) => setQLead(e.target.value)} />
          </Field>
          <Field label="Approve by (days before publish)">
            <input type="number" min={0} max={14} value={aLead} onChange={(e) => setALead(e.target.value)} />
          </Field>
          <Field label="Client timezone">
            <select value={tz} onChange={(e) => setTz(e.target.value)}>
              {TIMEZONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </div>
        {firstDate && (
          <div className="banner banner-info">
            First questions {agencyOnly ? '(to you)' : 'email'}: {pretty(questionsDate, '7am')} · First post publishes: {pretty(firstDate, '8am')}
            {monthly && monthPpd > 1 ? ` (${monthPpd} posts)` : ''}
          </div>
        )}
      </section>

      <div className="review-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !!createdId}>
          {busy ? 'Saving…' : 'Save and send invite'}
        </button>
        <SaveStatus status={saved.status} />
        <Link to="/" className="btn">Cancel</Link>
      </div>
    </form>
  );
}
